import EventEmitter from "eventemitter3";
import components from "../components/_map";
import debounce from "lodash/debounce";
import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import Utils from "../utils/utils";

gsap.registerPlugin(CustomEase);

/** Composants nécessaires au loader / intro hero — boot immédiat. */
const CRITICAL_COMPONENTS = new Set(["site-loader", "reveal", "header"]);

const POST_LCP_IDLE_TIMEOUT_MS = 2000;
const POST_LCP_FAILSAFE_MS = 3000;

export default class AppController extends EventEmitter {
	constructor() {
		super();
		if (!AppController.singleton) {
			AppController.singleton = this;
			globalThis.__drieuxApp = this;
			this.components = [];
			this.gsap = gsap;
			this.currentController = document.body.getAttribute("data-controller");
			this.components_map = components;
			this.$scroll = null;
			this.$dropdowns = null;
			this.$menu = null;
			this.$pageTransition = null;
			this._deferredReady = null;
			this._scrollLockCount = 0;
			this._scrollLockY = 0;
			this.ready = this.init();
			this._deferredReady = this.ready.then(() => this._runDeferredAfterLcp());
		}
		return AppController.singleton;
	}

	async init() {
		this.lockScrollTop();
		this.isMobile = window.matchMedia("(max-width: 1024px), (pointer: coarse)").matches;

		this.forceScrollTop({ hard: true });
		this.initAppEvents();
		this.initDomElements();
		this.initViewportVhVar();
		this.initDetect();

		/* Critique LCP / intro — pas de Barba / Lenis / menu / CF7 ici */
		await this.instantiateComponents({ criticalOnly: true });

		window.addEventListener("load", () => this.forceScrollTop({ hard: false }), { once: true });
		window.addEventListener("pageshow", (e) => {
			if (e.persisted) this.forceScrollTop({ hard: false });
		});
	}

	/**
	 * Attend site-loader:reveal (ou timeout), puis requestIdleCallback.
	 */
	_waitForPostLcp() {
		return new Promise((resolve) => {
			let settled = false;
			const finish = () => {
				if (settled) return;
				settled = true;
				const go = () => resolve();
				if (typeof requestIdleCallback === "function") {
					requestIdleCallback(go, { timeout: POST_LCP_IDLE_TIMEOUT_MS });
				} else {
					setTimeout(go, 1);
				}
			};

			if (window.__drieuxLoaderReveal || window.__drieuxLoaderDone) {
				finish();
				return;
			}

			window.addEventListener("site-loader:reveal", finish, { once: true });
			setTimeout(finish, POST_LCP_FAILSAFE_MS);
		});
	}

	async _runDeferredAfterLcp() {
		await this._waitForPostLcp();

		const [{ default: Dropdowns }, { default: SiteMenu }, { default: PageTransition }, lenisMod] =
			await Promise.all([
				import("../components/dropdowns"),
				import("../components/site-menu"),
				import("../transitions/page-transition"),
				this.isMobile ? Promise.resolve({ default: null }) : import("../components/lenis-scroll"),
			]);

		/* Horloge unique (Tempus) pour GSAP — après le loader, bascule sans saut */
		const { bridgeGsap } = await import("../motion/ticker");
		bridgeGsap();

		const LCS = lenisMod?.default;
		if (!this.isMobile && LCS) {
			this.$scroll = new LCS();
			this.forceScrollTop({ hard: false });
			this.emit("lenis:ready");
		}

		this.$dropdowns = new Dropdowns();
		this.initTextareas();
		this.$menu = new SiteMenu(this);
		this.$pageTransition = new PageTransition(this);

		const { initContactFormTracking } = await import("../utils/tracking");
		initContactFormTracking();

		await this.instantiateComponents({ criticalOnly: false });
		await this.initBarba();
	}

	/** Promise résolue quand Barba / menu / etc. sont prêts (post-LCP). */
	get deferredReady() {
		return this._deferredReady || Promise.resolve();
	}

	lockScrollTop() {
		try {
			if ("scrollRestoration" in history) {
				history.scrollRestoration = "manual";
			}
		} catch {
			/* ignore */
		}
	}

	_currentScrollY() {
		const lenisY = this.$scroll?.lenis?.scroll;
		if (typeof lenisY === "number") return lenisY;
		return window.scrollY || document.documentElement?.scrollTop || document.body?.scrollTop || 0;
	}

	forceScrollTop({ hard = false } = {}) {
		try {
			if (!hard && this._currentScrollY() > 8) return;

			window.scrollTo(0, 0);
			if (document.documentElement) document.documentElement.scrollTop = 0;
			if (document.body) document.body.scrollTop = 0;
			const lenis = this.$scroll?.lenis;
			if (lenis?.scrollTo) {
				lenis.scrollTo(0, { immediate: true, force: true, lock: false });
			}
			this.emit("scroll", { scroll: 0 });
			window.dispatchEvent(new Event("scroll"));
		} catch {
			/* ignore */
		}
	}

	initDomElements() {}

	initAppEvents() {
		this._emitResize = debounce((event) => {
			this.emit("resize", event);
		}, 16);
		window.addEventListener("resize", this._emitResize);

		this._emitScroll = (event) => {
			if (this.$scroll?.lenis) return;
			this.emit("scroll", event);
		};
		window.addEventListener("scroll", this._emitScroll, { passive: true });

		window.onhashchange = () => {
			this.emit("hash-change", window.location.href);
		};

		this.on("scrollTo", (elt) => {
			if (this.$scroll) {
				this.$scroll.scrollTo(elt);
			} else {
				window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
			}
		});

		this.on("disable-scroll", () => this._disableScroll());
		this.on("enable-scroll", () => this._enableScroll());
	}

	_isCoarseOrIOS() {
		return (
			/iPad|iPhone|iPod/.test(navigator.userAgent) ||
			window.matchMedia("(pointer: coarse)").matches
		);
	}

	_needsFixedScrollLock() {
		return (
			this._isCoarseOrIOS() ||
			window.matchMedia("(max-width: 1189.98px)").matches
		);
	}

	_disableScroll() {
		this._scrollLockCount += 1;
		if (this._scrollLockCount > 1) return;

		this._scrollLockY = this._currentScrollY();
		document.body.classList.add("disable-scroll");
		document.documentElement.classList.add("disable-scroll");

		if (this._needsFixedScrollLock()) {
			document.body.style.top = `-${this._scrollLockY}px`;
			document.body.classList.add("fixed");
		}

		this.$scroll?.disable();
	}

	_enableScroll() {
		if (this._scrollLockCount <= 0) return;

		this._scrollLockCount -= 1;
		if (this._scrollLockCount > 0) return;

		this._unlockScrollDom({ restoreY: true });
	}

	/**
	 * Filet post-transition : reset le refcount (menu + PT peuvent laisser count > 0).
	 * @param {{ y?: number }} opts — y forcé après unlock (souvent 0 sous rideau)
	 */
	forceEnableScroll({ y = 0 } = {}) {
		this._scrollLockCount = 0;
		this._scrollLockY = typeof y === "number" ? y : 0;
		/* Sync claims locaux (menu) — sinon openMenu skip le prochain disable */
		if (this.$menu) this.$menu._scrollLocked = false;
		this._unlockScrollDom({ restoreY: false });
		try {
			window.scrollTo(0, this._scrollLockY);
			this.$scroll?.lenis?.scrollTo?.(this._scrollLockY, {
				immediate: true,
				force: true,
				lock: false,
			});
		} catch {
			/* ignore */
		}
		/*
		 * count déjà 0 → _enableScroll no-op, mais les listeners (Reveal flush)
		 * doivent quand même tourner comme avant le force-unlock.
		 */
		this.emit("enable-scroll");
	}

	_unlockScrollDom({ restoreY = false } = {}) {
		const savedY = this._scrollLockY;
		const hadFixed = document.body.classList.contains("fixed");

		document.body.classList.remove("disable-scroll", "fixed");
		document.documentElement.classList.remove("disable-scroll");
		document.body.style.top = "";

		this.$scroll?.enable();

		if (restoreY && hadFixed) {
			window.scrollTo(0, savedY);
			this.$scroll?.lenis?.scrollTo?.(savedY, {
				immediate: true,
				force: true,
				lock: false,
			});
		}
	}

	initTextareas() {
		const tx = document.getElementsByTagName("textarea");
		for (let i = 0; i < tx.length; i++) {
			tx[i].setAttribute("style", "height:" + tx[i].scrollHeight + "px;overflow-y:hidden;");
			tx[i].addEventListener("input", OnInput, false);
		}

		function OnInput() {
			this.style.height = "auto";
			this.style.height = this.scrollHeight + "px";
		}
	}

	/**
	 * @param {{ criticalOnly?: boolean }} opts
	 * criticalOnly=true → loader/header/reveal seulement
	 * criticalOnly=false → le reste (skip déjà instanciés)
	 */
	async instantiateComponents({ criticalOnly = false } = {}) {
		const nodes = document.body.querySelectorAll("[data-component]");
		const jobs = [];
		/*
		 * Pendant l'enter Barba, l'ancien container est encore dans le DOM : ne jamais
		 * ré-instancier ses composants (sinon instances orphelines : ticks, IO, mesures fausses).
		 */
		const containers = document.querySelectorAll('[data-barba="container"]');
		const current = containers.length ? containers[containers.length - 1] : null;

		nodes.forEach((el) => {
			const owner = el.closest('[data-barba="container"]');
			if (owner && current && owner !== current) return;

			const names = (el.getAttribute("data-component") || "")
				.trim()
				.split(/\s+/)
				.filter(Boolean);

			names.forEach((name) => {
				const isCritical = CRITICAL_COMPONENTS.has(name);
				if (criticalOnly && !isCritical) return;
				if (!criticalOnly && isCritical) return;

				const alreadyInstantiated = this.components.some(
					(component) => component.$el === el && component.__componentName === name
				);
				if (alreadyInstantiated) return;

				if (name === "site-loader" && (el.classList.contains("loaded") || window.__drieuxLoaderDone)) {
					return;
				}

				const loader = this.components_map[name];
				if (typeof loader !== "function") return;

				jobs.push(
					loader()
						.then((mod) => {
							const Ctor = mod?.default || mod;
							if (typeof Ctor !== "function") return;
							const instance = new Ctor(el);
							instance.__componentName = name;
							this.components.push(instance);
						})
						.catch((err) => {
							console.error(`[drieux] component "${name}" failed to load`, err);
						})
				);
			});
		});

		await Promise.all(jobs);
		if (!criticalOnly) this.markScrollableRegions();
	}

	/**
	 * A11y (axe scrollable-region-focusable) : un carrousel à défilement natif sans
	 * élément focusable doit pouvoir recevoir le focus pour défiler au clavier.
	 * tabindex posé uniquement quand la zone déborde réellement (mobile), retiré sinon.
	 * (sync interroge le document à chaque appel : un seul listener resize suffit)
	 */
	markScrollableRegions() {
		const sync = () => {
			document
				.querySelectorAll("[data-three-push-track], .c-certifications__logos, .swiper-mobile")
				.forEach((el) => {
					const scrollable = el.scrollWidth > el.clientWidth + 1 && /(auto|scroll)/.test(getComputedStyle(el).overflowX);
					const hasFocusable = !!el.querySelector("a[href], button, input, select, textarea, [tabindex]");
					if (scrollable && !hasFocusable) {
						el.setAttribute("tabindex", "0");
						el.setAttribute("role", "region");
						if (!el.hasAttribute("aria-label")) {
							const heading = el.closest("section")?.querySelector("h2, h3");
							el.setAttribute("aria-label", (heading?.textContent || "Contenu défilant").trim().slice(0, 80));
						}
					} else if (el.getAttribute("tabindex") === "0" && el.getAttribute("role") === "region") {
						el.removeAttribute("tabindex");
						el.removeAttribute("role");
					}
				});
		};
		sync();
		if (!this._scrollRegionsResize) {
			let t = null;
			this._scrollRegionsResize = () => {
				clearTimeout(t);
				t = setTimeout(sync, 200);
			};
			window.addEventListener("resize", this._scrollRegionsResize, { passive: true });
		}
	}

	destroyPageComponents() {
		this.components = this.components.filter((c) => {
			if (c?.__componentName === "site-loader" || c?.$el?.getAttribute?.("data-component") === "site-loader") {
				return true;
			}
			/* Un destroy qui échoue ne doit jamais laisser vivre les autres (fuites, ticks orphelins) */
			try {
				c?.destroy?.();
			} catch (err) {
				console.error(`[drieux] destroy "${c?.__componentName}" failed`, err);
			}
			return false;
		});
	}

	initViewportVhVar() {
		this.updateViewportVhVar();
		window.addEventListener("resize", () => {
			this.updateViewportVhVar();
		});
	}

	updateViewportVhVar() {
		let vh = window.innerHeight;
		document.documentElement.style.setProperty("--vh", `${vh / 100}px`);
	}

	initDetect() {
		window.addEventListener("resize", () => {
			this.detectDevice();
		});
		this.detectDevice();
	}

	detectDevice = () => {
		let landscape = window.matchMedia("(orientation: landscape)");
		document.documentElement.setAttribute(
			"data-device-notice",
			Utils.deviceType() == "mobile" && landscape.matches && window.innerWidth < 1000
		);
	};

	async initBarba() {
		const [{ default: barba }, { default: barbaPrefetch }, { initContactFormTracking }] = await Promise.all([
			import("@barba/core"),
			import("@barba/prefetch"),
			import("../utils/tracking"),
		]);

		barba.hooks.afterLeave((data) => {
			const js = data.next.container.querySelectorAll("script.reload-on-ajax");
			[].forEach.call(js, function (script) {
				try {
					window.eval(script.innerHTML);
				} catch (e) {
					/* Silent fail */
				}
			});
		});

		barba.hooks.after(() => {
			this.forceScrollTop({ hard: false });
			if (this.$scroll) this.$scroll.update();
		});

		barba.hooks.afterEnter((data) => {
			try {
				const title = data?.next?.container?.getAttribute("data-page-title");
				if (title) {
					document.title = title;
				}

				if (this.$pageTransition) {
					const canonical = this.$pageTransition._nextCanonical;
					const lang = this.$pageTransition._nextLang;
					const dir = this.$pageTransition._nextDir;
					if (canonical) {
						let link = document.querySelector('link[rel="canonical"]');
						if (!link) {
							link = document.createElement("link");
							link.rel = "canonical";
							document.head.appendChild(link);
						}
						link.href = canonical;
					}
					if (lang != null) {
						document.documentElement.setAttribute("lang", lang);
					}
					if (dir != null) {
						document.documentElement.setAttribute("dir", dir);
					}
					this.$pageTransition._nextCanonical = null;
					this.$pageTransition._nextLang = null;
					this.$pageTransition._nextDir = null;
				}

				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: "virtual_pageview",
					page_path: window.location.pathname + window.location.search,
					page_title: document.title,
				});
			} catch (e) {
				/* Silent fail */
			}
		});

		barba.use(barbaPrefetch);
		barba.init({
			debug: false,
			timeout: 8000,
			prefetchIgnore: false,
			prevent: ({ el }) => {
				if (el && (el.hash || el.hasAttribute("data-popin-hash"))) {
					return true;
				}
				if (el && el.href) {
					try {
						const url = new URL(el.href, window.location.href);
						const currentUrl = new URL(window.location.href);
						if (
							url.pathname === currentUrl.pathname &&
							url.search === currentUrl.search &&
							url.hash !== currentUrl.hash
						) {
							return true;
						}
					} catch (e) {
						/* Silent fail */
					}
				}
				return el.getAttribute("href") == "#" || (el.getAttribute("href") && el.getAttribute("href").includes("billet"));
			},
			transitions: [
				{
					name: "default",
					leave: (data) => {
						if (document.activeElement) document.activeElement.blur();
						document.querySelectorAll(".c-site-menu :focus").forEach((el) => el.blur?.());

						if (this.$dropdowns?.closeAllDropdowns) {
							this.$dropdowns.closeAllDropdowns();
						}

						const closeMenuUnderCurtain = () => {
							/* Rideau déjà plein écran (z > menu) — fermer sans anim visible */
							this.emit("close-menu");
							this.$menu?.forceCloseMenu?.();
						};

						const TRANSITION_TIMEOUT_MS = 8000;
						const transitionPromise = new Promise((resolve) => {
							if (this.$pageTransition) {
								this.$pageTransition.show(data).then(() => {
									closeMenuUnderCurtain();
									this.destroyPageComponents();
									if (this.$dropdowns?.destroy) {
										this.$dropdowns.destroy();
									}
									resolve();
								});
							} else {
								closeMenuUnderCurtain();
								this.destroyPageComponents();
								if (this.$dropdowns?.destroy) {
									this.$dropdowns.destroy();
								}
								resolve();
							}
						});
						const timeoutPromise = new Promise((resolve) => {
							setTimeout(() => {
								closeMenuUnderCurtain();
								resolve();
							}, TRANSITION_TIMEOUT_MS);
						});
						return Promise.race([transitionPromise, timeoutPromise]).then(() => {
							this.destroyPageComponents();
						});
					},
					enter: async (data) => {
						if (this.$pageTransition) {
							await this.$pageTransition.updateMenuAndPopinsBeforeTransition(data);
						}

						const { default: Dropdowns } = await import("../components/dropdowns");
						if (this.$dropdowns?.init) {
							this.$dropdowns.init();
						} else if (this.$dropdowns?.refresh) {
							this.$dropdowns.refresh();
						} else {
							this.$dropdowns = new Dropdowns();
						}
						this.$dropdowns?.closeAllDropdowns?.();
						this.$menu?.forceCloseMenu?.();

						/* Après Barba : tout (y compris critique page) */
						await this.instantiateComponents({ criticalOnly: true });
						await this.instantiateComponents({ criticalOnly: false });
						this.initTextareas();

						if (data?.next?.html) {
							Utils.setNewAttrs(data.next.html);
						}
					},
					after: async () => {
						if (this.$pageTransition) {
							await this.$pageTransition.hide();
						}

						this.updateViewportVhVar();
						this.detectDevice();
						initContactFormTracking();
					},
				},
			],
		});
	}

	static getInstance() {
		return AppController.singleton;
	}
}
